import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import type { ParametersBlockProps } from '../types';
import styles from './ParametersBlock.module.css';
import { InfoIcon, ChevronDownIcon, ClockIcon, UpscaleIcon, MagnificIcon } from '../icons';
import { GigapixelIcon, BloomIcon, WonderIcon } from '../../../pages/Upscale/components/TopazSection/TopazFamilyIcons';

// Model provider icons (same as in ImageComposer)
import OpenAIIcon from '../../../assets/icons/ai_icons/open_ai.svg';
import GoogleIcon from '../../../assets/icons/ai_icons/Veo.svg';
import SoraIcon from '../../../assets/icons/ai_icons/Sora.svg';
import ByteDanceIcon from '../../../assets/icons/ai_icons/ByteDance.svg';
import HiggsfieldIcon from '../../../assets/icons/ai_icons/higgs_star.svg';
import RunwayIcon from '../../../assets/icons/ai_icons/Runway.svg';
import ReveIcon from '../../../assets/icons/ai_icons/Reve.svg';
import KlingIcon from '../../../assets/icons/ai_icons/Kling.svg';
import FluxIcon from '../../../assets/icons/ai_icons/Flux.svg';
import MidjourneyIcon from '../../../assets/icons/ai_icons/Midjourney.svg';
import GrokIcon from '../../../assets/icons/ai_icons/grok.svg';

/** Map provider/model config key to icon asset */
const PROVIDER_ICON_MAP: Record<string, string> = {
  google: GoogleIcon,
  openai: OpenAIIcon,
  bytedance: ByteDanceIcon,
  higgsfield: HiggsfieldIcon,
  runway: RunwayIcon,
  reve: ReveIcon,
  kling: KlingIcon,
  flux: FluxIcon,
  'motion-control': KlingIcon,
  veo: GoogleIcon,
  sora: SoraIcon,
  seedance: ByteDanceIcon,
  wan: ByteDanceIcon,
  'kling-o1-aiml': KlingIcon,
  'freepik-kling': KlingIcon,
  'nano-banana-pro': GoogleIcon,
  'nano-banana-2': GoogleIcon,
  'nano-banana': GoogleIcon,
  'imagen-4-fast': GoogleIcon,
  'imagen-4-ultra': GoogleIcon,
  'gpt-image-1.5': OpenAIIcon,
  'gpt-image-2': OpenAIIcon,
  'openai-gpt-image-2': OpenAIIcon,
  // Upscale providers — SVG asset-based.
  // Backend stores provider as '{name}-upscale' for upscale results.
  seedvr: ByteDanceIcon,
  'seedvr-2': ByteDanceIcon,
  'seedvr-upscale': ByteDanceIcon,
  'seedream-4.5': ByteDanceIcon,
  'seedream-4': ByteDanceIcon,
  'higgsfield-soul': HiggsfieldIcon,
  'runway-gen4-image': RunwayIcon,
  'runway-gen4-image-turbo': RunwayIcon,
  'kling-v2-1': KlingIcon,
  'kling-v2-6': KlingIcon,
  'kling-o1': KlingIcon,
  'kling-v3-std': KlingIcon,
  'kling-v3-pro': KlingIcon,
  'flux-2-max': FluxIcon,
  'flux-2-pro': FluxIcon,
  'flux-2-flex': FluxIcon,
  'flux-2-klein-9b': FluxIcon,
  grok: GrokIcon,
  xai: GrokIcon,
  'grok-imagine-image': GrokIcon,
  'grok-imagine-image-pro': GrokIcon,
  'grok-imagine-video': GrokIcon,
  'gemini-nano-banana': GoogleIcon,
  'gemini-imagen': GoogleIcon,
  'openai-gpt-image': OpenAIIcon,
  'reve-edit': ReveIcon,
  'reve-remix': ReveIcon,
  'reve-create': ReveIcon,
  'veo-3': GoogleIcon,
  'veo-3-fast': GoogleIcon,
  'veo-3.1': GoogleIcon,
  'veo-3.1-fast': GoogleIcon,
  'veo-3.1-lite': GoogleIcon,
  'seedance-1-5-pro-251215': ByteDanceIcon,
  'seedance-1-0-pro-fast-251015': ByteDanceIcon,
  'seedance-1-0-pro-250528': ByteDanceIcon,
  'seedance-1-0-lite-250428': ByteDanceIcon,
  'wan-2-1': ByteDanceIcon,
  'wan-2-1-turbo': ByteDanceIcon,
  'sora-2': SoraIcon,
  'sora-2-pro': SoraIcon,
  'runway-turbo': RunwayIcon,
  'runway-aleph': RunwayIcon,
  'runway-act-two': RunwayIcon,
  midjourney: MidjourneyIcon,
  'midjourney-v7': MidjourneyIcon,
  'midjourney-v6.1': MidjourneyIcon,
  'midjourney-v6': MidjourneyIcon,
};

/** Brand icons rendered as React components (SVGs with gradients / complex markup).
 *  Keep in sync with Upscale page — these live in `pages/Upscale/components/TopazSection/TopazFamilyIcons.tsx`. */
const PROVIDER_COMPONENT_MAP: Record<string, React.ComponentType> = {
  'topaz-gigapixel': GigapixelIcon,
  'topaz-wonder': WonderIcon,
  'topaz-bloom': BloomIcon,
  magnific: MagnificIcon,
};

/** Upscale history: worker stores provider as 'ai-upscale' regardless of the engine,
 *  and the real engine lives in `model` (e.g. "topaz/gigapixel/standard-max", "seedvr", "2x").
 *  Also handles the legacy '*-upscale' provider strings from the queue layer. */
function resolveUpscaleIcon(provider?: string, model?: string): {
  component?: React.ComponentType;
  asset?: string;
} {
  const p = (provider || '').toLowerCase();
  const m = (model || '').toLowerCase();

  // Topaz family — look for explicit prefix or family keyword
  if (p === 'topaz' || p === 'topaz-upscale' || m.startsWith('topaz/') || m.includes('gigapixel') || m.includes('wonder') || m.includes('bloom')) {
    if (m.includes('wonder')) return { component: WonderIcon };
    if (m.includes('bloom')) return { component: BloomIcon };
    return { component: GigapixelIcon }; // flagship default
  }
  // SeedVR
  if (p === 'seedvr' || p === 'seedvr-2' || p === 'seedvr-upscale' || m.includes('seedvr')) {
    return { asset: ByteDanceIcon };
  }
  // Freepik/Magnific — provider 'ai-upscale' with scale-factor model (e.g. '2x', '4x')
  // or explicit magnific/freepik labels.
  if (p === 'magnific' || p === 'freepik-upscale' || p === 'ai-upscale' || /^\d+x?$/i.test(m)) {
    return { component: MagnificIcon };
  }
  return {};
}

interface Row {
  label: string;
  value: React.ReactNode;
}

// Number of rows shown when collapsed; the rest goes behind "Show all"
const COLLAPSED_ROWS = 4;

export const ParametersBlock: React.FC<ParametersBlockProps> = ({
  model,
  provider,
  time,
  format,
  quality,
  resolution,
  duration,
  stylize,
  chaos,
  weird,
  seed,
  niji,
  mjQuality,
  className,
}) => {
  const { t } = useTranslation('image');
  const [expanded, setExpanded] = useState(false);

  // Prefer explicit bucket lookups first, then the upscale resolver (handles ai-upscale / topaz/* / seedvr / Nx)
  const explicitProviderIcon = provider ? PROVIDER_ICON_MAP[provider] : null;
  const explicitProviderComponent = provider ? PROVIDER_COMPONENT_MAP[provider] : undefined;
  const upscaleResolved = resolveUpscaleIcon(provider, model);

  const providerIcon = explicitProviderIcon ?? upscaleResolved.asset ?? null;
  const ProviderComponent = explicitProviderComponent ?? upscaleResolved.component;

  // Final fallback for any upscaler without a brand icon — generic upscale glyph
  const isUpscaleProvider = !providerIcon && !ProviderComponent && !!provider
    && /topaz|magnific|gigapixel|wonder|bloom|upscal|seedvr/i.test(provider);

  const rows = useMemo<Row[]>(() => {
    const list: Row[] = [];

    // Model (with small provider icon)
    list.push({
      label: t('gallery.model'),
      value: (
        <span className={styles.modelValue}>
          {providerIcon ? (
            <img src={providerIcon} alt="" className={styles.providerIcon} />
          ) : ProviderComponent ? (
            <span className={styles.providerGlyph}><ProviderComponent /></span>
          ) : isUpscaleProvider ? (
            <span className={styles.providerGlyph}><UpscaleIcon /></span>
          ) : null}
          <span>{model}</span>
        </span>
      ),
    });

    if (duration) {
      list.push({ label: t('gallery.duration'), value: duration });
    } else if (time) {
      list.push({
        label: t('gallery.generationTime'),
        value: (
          <span className={styles.timeValue}>
            <ClockIcon />
            <span>{time}</span>
          </span>
        ),
      });
    }

    if (format) list.push({ label: t('gallery.format'), value: format });
    if (quality) {
      // low/medium/high → Low/Medium/High. Значения вроде "2K", "1080p", "Standard"
      // остаются как есть (их первая буква уже в нужном регистре).
      const qualityDisplay = quality.charAt(0).toUpperCase() + quality.slice(1);
      list.push({
        label: t('gallery.quality'),
        value: resolution ? (
          <span className={styles.qualityValue}>
            <span>{qualityDisplay}</span>
            <span className={styles.qualitySuffix}>{resolution}</span>
          </span>
        ) : qualityDisplay,
      });
    }

    // Midjourney-specific params
    const isMidjourney = provider?.startsWith('midjourney');
    const hasMjParams = isMidjourney && (
      stylize !== undefined || chaos !== undefined || weird !== undefined ||
      seed !== undefined || (niji && niji !== 'close') ||
      (mjQuality !== undefined && mjQuality !== 1)
    );
    if (hasMjParams) {
      if (stylize !== undefined) list.push({ label: 'Stylize', value: String(stylize) });
      if (chaos !== undefined && chaos > 0) list.push({ label: 'Chaos', value: String(chaos) });
      if (weird !== undefined && weird > 0) list.push({ label: 'Weird', value: String(weird) });
      if (seed !== undefined && seed >= 0) list.push({ label: 'Seed', value: String(seed) });
      if (niji && niji !== 'close') list.push({ label: 'Niji', value: `Niji ${niji}` });
      if (mjQuality !== undefined && mjQuality !== 1) list.push({ label: 'Quality', value: String(mjQuality) });
    }

    return list;
  }, [t, model, providerIcon, ProviderComponent, isUpscaleProvider, duration, time, format, quality, resolution, stylize, chaos, weird, seed, niji, mjQuality, provider]);

  const hasMore = rows.length > COLLAPSED_ROWS;
  const visibleRows = expanded || !hasMore ? rows : rows.slice(0, COLLAPSED_ROWS);

  return (
    <div className={clsx(styles.card, className)}>
      <div className={styles.header}>
        <span className={styles.headerIcon}><InfoIcon /></span>
        <span className={styles.headerLabel}>{t('gallery.params')}</span>
      </div>

      <div className={styles.rows}>
        {visibleRows.map((row, i) => (
          <div key={i} className={styles.row}>
            <span className={styles.rowLabel}>{row.label}</span>
            <span className={styles.rowValue}>{row.value}</span>
          </div>
        ))}
      </div>

      {hasMore && (
        <button
          type="button"
          className={styles.seeAllToggle}
          onClick={() => setExpanded((v) => !v)}
        >
          <span>{expanded ? t('gallery.hide') : t('gallery.show')}</span>
          <span className={clsx(styles.seeAllChevron, expanded && styles.rotated)}>
            <ChevronDownIcon />
          </span>
        </button>
      )}
    </div>
  );
};

export default ParametersBlock;
